import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  useAddTagMutation,
  useClientTags,
  useDeleteMutation,
  useEntityList,
  useMarkMutation,
  useReviewMutation,
} from '@/api/queries'
import { ConfirmDialog } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import { countLabel } from '@/lib/format'
import type { EntityType, Reviewable, ReviewStatus } from '@/types'
import type { StatusCounts, StatusFilter } from './StatusTabs'

export interface ReviewCollectionConfig<T> {
  /** Fields concatenated for the search box. Lowercased here, not by the caller. */
  searchText: (row: T) => string
  /** Singular noun for bulk copy and toasts: "program", "quote". */
  noun: string
  /**
   * Extra predicate — segment rail, media type, fact kind. Wrap it in
   * `useCallback` at the call site; it is a dependency of the row memo.
   */
  extraFilter?: (row: T) => boolean
  /**
   * Rows that belong to this surface at all. Unlike `extraFilter` it applies
   * before the counts, so the tabs do not count rows the page never shows
   * (Programs' merged duplicates). Wrap it in `useCallback` too.
   */
  scope?: (row: T) => boolean
  /**
   * Rows that share a key are one item on the page (Programs' degrees of one
   * program). A group matches a filter when any of its rows does, and then
   * every row in it is returned. Counts and toasts count groups, not rows.
   */
  groupBy?: (row: T) => string
  defaultStatus?: StatusFilter
}

export interface ReviewCollection<T> {
  status: StatusFilter
  setStatus: (next: StatusFilter) => void
  query: string
  setQuery: (next: string) => void
  tagFilter: string
  setTagFilter: (next: string) => void

  /** Filtered and ready to render. */
  rows: T[]
  /** All rows for this client in `scope`, before any filter. */
  all: T[]
  counts: StatusCounts
  tagPool: string[]

  selected: Set<string>
  setSelected: (next: Set<string>) => void
  clearSelection: () => void

  bulk: {
    approve: () => void
    reject: () => void
    addTag: (tag: string) => void
    requestDelete: () => void
  }
  /** Render this. It owns the delete confirmation and its pluralised copy. */
  deleteDialog: ReactNode

  setStatusFor: (id: string, status: ReviewStatus) => void
  setFavoriteFor: (id: string, favorite: boolean) => void
  setFlagFor: (id: string, flagged: boolean) => void

  isLoading: boolean
}

/**
 * Every piece of state the four review pages used to duplicate: the counts
 * memo, the filter memo, the selection set, the six bulk handlers, and the
 * delete dialog (docs/06-component-refactor.md §4.1).
 *
 * Change how bulk-approve reports success and you now edit one file.
 */
export function useReviewCollection<T extends Reviewable>(
  clientId: string,
  entity: EntityType,
  config: ReviewCollectionConfig<T>,
): ReviewCollection<T> {
  const { searchText, noun, extraFilter, scope, groupBy, defaultStatus = 'pending' } = config

  const { data, isLoading } = useEntityList(clientId, entity)
  const all = useMemo(() => {
    const rows = (data ?? []) as T[]
    return scope ? rows.filter(scope) : rows
  }, [data, scope])
  const { data: tagPool = [] } = useClientTags(clientId)

  const review = useReviewMutation(clientId, entity)
  const favorite = useMarkMutation(clientId, entity, 'favorite')
  const flag = useMarkMutation(clientId, entity, 'flagged')
  const addTag = useAddTagMutation(clientId, entity)
  const remove = useDeleteMutation(clientId, entity)
  const toast = useToast()

  const [status, setStatus] = useState<StatusFilter>(defaultStatus)
  const [query, setQuery] = useState('')
  const [tagFilter, setTagFilter] = useState('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState(false)

  /** How many items these rows are: rows, or distinct groups under `groupBy`. */
  const itemCount = useCallback(
    (rows: T[]) => (groupBy ? new Set(rows.map(groupBy)).size : rows.length),
    [groupBy],
  )

  /** Counts run over the unfiltered set so the tab numbers hold still while you type. */
  const counts = useMemo<StatusCounts>(
    () => ({
      pending: itemCount(all.filter((r) => r.status === 'pending')),
      approved: itemCount(all.filter((r) => r.status === 'approved')),
      rejected: itemCount(all.filter((r) => r.status === 'rejected')),
      favorites: itemCount(all.filter((r) => r.favorite)),
      flagged: itemCount(all.filter((r) => r.flagged)),
    }),
    [all, itemCount],
  )

  /** Filter order is fixed: status → extraFilter → tag → search. */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matching = all.filter((row) => {
      if (status === 'favorites') {
        if (!row.favorite) return false
      } else if (status === 'flagged') {
        if (!row.flagged) return false
      } else if (row.status !== status) return false
      if (extraFilter && !extraFilter(row)) return false
      if (tagFilter !== 'all' && !row.tags.includes(tagFilter)) return false
      if (needle && !searchText(row).toLowerCase().includes(needle)) return false
      return true
    })
    if (!groupBy) return matching
    const keys = new Set(matching.map(groupBy))
    return all.filter((row) => keys.has(groupBy(row)))
  }, [all, status, extraFilter, tagFilter, query, searchText, groupBy])

  /** "3 programs" for these selected ids, counted as items. */
  const selectionLabel = useCallback(
    (ids: string[]) => {
      const chosen = new Set(ids)
      return countLabel(itemCount(all.filter((r) => chosen.has(r.id))), noun)
    },
    [all, itemCount, noun],
  )

  const clearSelection = useCallback(() => setSelected(new Set()), [])

  /** Selection clears on success, not optimistically — a failed bulk keeps its rows selected. */
  const bulkVerdict = useCallback(
    (next: ReviewStatus) => {
      const ids = [...selected]
      const label = selectionLabel(ids)
      review.mutate(
        { ids, status: next },
        {
          onSuccess: () => {
            toast.push(`${next === 'approved' ? 'Approved' : 'Rejected'} ${label}.`)
            clearSelection()
          },
          onError: () => toast.push(`Could not update ${label}.`, 'error'),
        },
      )
    },
    [selected, selectionLabel, review, toast, clearSelection],
  )

  const bulk = useMemo(
    () => ({
      approve: () => bulkVerdict('approved'),
      reject: () => bulkVerdict('rejected'),
      addTag: (tag: string) => {
        const ids = [...selected]
        addTag.mutate(
          { ids, tag },
          { onSuccess: () => toast.push(`Tagged ${selectionLabel(ids)} “${tag}”.`) },
        )
      },
      requestDelete: () => setConfirmDelete(true),
    }),
    [bulkVerdict, selected, addTag, toast, selectionLabel],
  )

  const setStatusFor = useCallback(
    (id: string, next: ReviewStatus) => review.mutate({ ids: [id], status: next }),
    [review],
  )

  const setFavoriteFor = useCallback(
    (id: string, next: boolean) => favorite.mutate({ id, on: next }),
    [favorite],
  )

  const setFlagFor = useCallback(
    (id: string, next: boolean) => flag.mutate({ id, on: next }),
    [flag],
  )

  const deleteDialog = (
    <ConfirmDialog
      open={confirmDelete}
      onClose={() => setConfirmDelete(false)}
      onConfirm={async () => {
        const ids = [...selected]
        await remove.mutateAsync(ids)
        setConfirmDelete(false)
        toast.push(`Deleted ${selectionLabel(ids)}.`)
        clearSelection()
      }}
      title={`Delete ${selectionLabel([...selected])}?`}
      description={`Deleted ${noun}s are removed from the library entirely. Rejecting instead keeps the record and its review history.`}
      confirmLabel="Delete"
      destructive
      busy={remove.isPending}
    />
  )

  return {
    status,
    setStatus,
    query,
    setQuery,
    tagFilter,
    setTagFilter,
    rows,
    all,
    counts,
    tagPool,
    selected,
    setSelected,
    clearSelection,
    bulk,
    deleteDialog,
    setStatusFor,
    setFavoriteFor,
    setFlagFor,
    isLoading,
  }
}
